/**
 * Structural snapshot of a value about to be published to the data layer.
 *
 * WHY THIS EXISTS (DE751628 follow-up)
 * An event producer normally derives its payload from live application state — the CDL relay
 * contract describes `event.componentContext` as "originating PBC state minus event". Publishing
 * that payload with a shallow copy hands the data layer a reference to state that the producer,
 * and every relay in the chain, still holds. The envelope then stops being a record of what
 * happened:
 *  - a later mutation of that state retroactively rewrites an event already published;
 *  - anything that strips a field off what it received reaches back into the producer's state,
 *    and a `eventMap` entry that is a plain object rather than a function is shared by every
 *    dispatch, so one such strip removes attribution from all later events of that action type.
 *
 * Cloning the payload before it is handed over ends that coupling in one place.
 *
 * WHY NOT `structuredClone` OR A JSON ROUND-TRIP
 * `structuredClone` throws a `DataCloneError` on a function or a DOM node, so a payload that
 * carries one would start failing where it previously published fine. A JSON round-trip silently
 * drops keys whose value is `undefined` and rewrites `Date` values as strings, which would change
 * the published shape — `buildInitialDataLayerV3` deliberately emits `undefined` values.
 *
 * This clone therefore copies only plain objects and arrays — the data-carrying shapes — and
 * passes anything else (primitives, functions, `Date`s, class instances) through by reference. It
 * is a snapshot of the payload's structure, not a guarantee that every leaf is independent.
 */

/** Any non-null value that reached the `typeof value === 'object'` branch below. */
type UnknownObject = Record<string, unknown>;

/**
 * Plain data containers are safe to rebuild. A class instance is not: rebuilding it as a bare
 * object would strip its prototype, so it is passed through untouched instead.
 */
const isPlainObject = (value: UnknownObject): boolean => {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
};

/**
 * Deep-copies plain objects and arrays, preserving keys whose value is `undefined`.
 *
 * @param value - The value to snapshot
 * @param seen - Tracks objects already copied, so a payload that references itself yields a
 * correspondingly self-referencing copy rather than recursing until the stack is exhausted
 */
export const safeClone = <T>(value: T, seen: WeakMap<UnknownObject, any> = new WeakMap()): T => {
    if (value === null || typeof value !== 'object') {
        return value;
    }

    const source = (value as unknown) as UnknownObject;
    if (seen.has(source)) {
        return seen.get(source);
    }

    if (Array.isArray(value)) {
        const copy: any[] = [];
        seen.set(source, copy);
        value.forEach((entry, index) => {
            copy[index] = safeClone(entry, seen);
        });
        return (copy as unknown) as T;
    }

    if (!isPlainObject(source)) {
        return value;
    }

    const copy: any = {};
    seen.set(source, copy);
    Object.keys(source).forEach((key) => {
        copy[key] = safeClone((source as any)[key], seen);
    });
    return copy as T;
};
